import { Card, Empty, Input, Select, Space, Table, Tag, Typography, Grid } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  abortLatestRequest,
  ANALYTICS_INFO_TEXT,
  beginLatestRequest,
  ANALYTICS_RESOLUTION_DISPLAY_CONFIG,
  ANALYTICS_RESOLUTION_ENUM,
  DATE_DISPLAY_FORMAT,
  EXTERNAL_PROBLEMS_API_BASE,
  EXTERNAL_PROBLEMS_PAGINATION_CONFIG,
  PLATFORM_DISPLAY_NAME_MAP,
  REGISTERED_PLATFORM_FILTER_OPTIONS,
  reportUnlessCancelled,
  RESOLUTION_FILTER_OPTIONS,
  SORT_ORDER_ENUM,
  STUDENT_SORT_BY_ENUM,
  STUDENT_SORT_ORDER_BY_TABLE_ORDER,
  STUDENT_TABLE_SORT_COLUMN_MAP,
  formatHitRatioPercent,
  formatPercent,
  sumColumnWidths,
  COLUMN_WIDTH_ENUM
} from "../helper";
import InfoHint, { LabelWithHint } from "../InfoHint";

const { Text } = Typography;

// The count columns share their explanations with the per-platform table on
// the Analytics overview, so both read the same sentence.
const {
  [ANALYTICS_RESOLUTION_ENUM.RESOLVED]: { infoText: RESOLVED_INFO_TEXT },
  [ANALYTICS_RESOLUTION_ENUM.PARTIAL]: { infoText: PARTIAL_INFO_TEXT },
  [ANALYTICS_RESOLUTION_ENUM.NOT_FOUND]: { infoText: NOT_FOUND_INFO_TEXT },
  [ANALYTICS_RESOLUTION_ENUM.INVALID_URL]: { infoText: INVALID_URL_INFO_TEXT }
} = ANALYTICS_RESOLUTION_DISPLAY_CONFIG;

const STUDENT_FILTER_INITIAL_STATE = Object.freeze({
  platform: undefined,
  resolution: undefined,
  searchTerm: ""
});

const STUDENT_SORT_INITIAL_STATE = Object.freeze({
  sortBy: STUDENT_SORT_BY_ENUM.ATTEMPT_COUNT,
  sortOrder: SORT_ORDER_ENUM.DESC
});

/** Column widths of the student table; `scroll.x` is their sum. */
const STUDENT_TABLE_COLUMN_WIDTHS = Object.freeze({
  student: COLUMN_WIDTH_ENUM.LONG,
  attemptCount: COLUMN_WIDTH_ENUM.COUNT,
  resolvedCount: COLUMN_WIDTH_ENUM.COUNT,
  partialCount: COLUMN_WIDTH_ENUM.SHORT,
  notFoundCount: COLUMN_WIDTH_ENUM.COUNT,
  invalidUrlCount: COLUMN_WIDTH_ENUM.COUNT,
  invalidUrlShare: COLUMN_WIDTH_ENUM.WIDE,
  hitRatio: COLUMN_WIDTH_ENUM.SHORT,
  platforms: COLUMN_WIDTH_ENUM.LONG,
  lastAttemptedAt: COLUMN_WIDTH_ENUM.DATE
});

/**
 * Builds a sortable, centred count column that sorts descending first, which
 * is the direction the page's two useful sorts read.
 *
 * @param {object} params
 * @param {string} params.key - Row field and column key.
 * @param {string} params.label
 * @param {string} params.infoText
 * @param {number} params.width
 * @param {Array<string>} params.responsive - antd breakpoints the column shows at.
 * @returns {object} An antd column definition.
 */
const buildSortableColumn = ({ key, label, infoText, width, responsive }) => ({
  title: <LabelWithHint label={label} infoText={infoText} />,
  dataIndex: key,
  key,
  width,
  align: "center",
  responsive,
  sorter: true,
  sortDirections: ["descend", "ascend"]
});

/**
 * Columns of the per-student table. Their widths sum to the table's `scroll.x`,
 * because antd renders header and body as two tables that each redistribute
 * slack on their own.
 */
const STUDENT_TABLE_COLUMNS = [
  {
    title: "Student",
    key: "student",
    width: STUDENT_TABLE_COLUMN_WIDTHS.student,
    render: (_, { name, email, isAccountDeleted, userId }) => (
      <Space direction="vertical" size={0}>
        {isAccountDeleted && (
          <Space size={4}>
            <Tag color="default">Deleted account</Tag>
            <InfoHint title={ANALYTICS_INFO_TEXT.DELETED_ACCOUNT} />
          </Space>
        )}
        {name && <Text strong>{name}</Text>}
        {email && <Text type="secondary">{email}</Text>}
        {!name && !email && (
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {userId}
          </Text>
        )}
      </Space>
    )
  },
  buildSortableColumn({
    key: "attemptCount",
    label: "Attempts",
    infoText: ANALYTICS_INFO_TEXT.ATTEMPT_COUNT,
    width: STUDENT_TABLE_COLUMN_WIDTHS.attemptCount,
    responsive: ["sm"]
  }),
  buildSortableColumn({
    key: "resolvedCount",
    label: "Resolved",
    infoText: RESOLVED_INFO_TEXT,
    width: STUDENT_TABLE_COLUMN_WIDTHS.resolvedCount,
    responsive: ["sm"]
  }),
  buildSortableColumn({
    key: "partialCount",
    label: "Partial",
    infoText: PARTIAL_INFO_TEXT,
    width: STUDENT_TABLE_COLUMN_WIDTHS.partialCount,
    responsive: ["md"]
  }),
  buildSortableColumn({
    key: "notFoundCount",
    label: "Not Found",
    infoText: NOT_FOUND_INFO_TEXT,
    width: STUDENT_TABLE_COLUMN_WIDTHS.notFoundCount,
    responsive: ["md"]
  }),
  buildSortableColumn({
    key: "invalidUrlCount",
    label: "Invalid URL",
    infoText: INVALID_URL_INFO_TEXT,
    width: STUDENT_TABLE_COLUMN_WIDTHS.invalidUrlCount,
    responsive: ["lg"]
  }),
  {
    title: (
      <LabelWithHint label="Invalid URL Share" infoText={ANALYTICS_INFO_TEXT.INVALID_URL_SHARE} />
    ),
    dataIndex: "invalidUrlShare",
    key: "invalidUrlShare",
    width: STUDENT_TABLE_COLUMN_WIDTHS.invalidUrlShare,
    align: "center",
    responsive: ["lg"],
    render: formatPercent
  },
  {
    ...buildSortableColumn({
      key: "hitRatio",
      label: "Hit Ratio",
      infoText: ANALYTICS_INFO_TEXT.HIT_RATIO,
      width: STUDENT_TABLE_COLUMN_WIDTHS.hitRatio,
      responsive: ["sm"]
    }),
    render: (hitRatio) => formatHitRatioPercent(hitRatio)
  },
  {
    title: <LabelWithHint label="Platforms" infoText={ANALYTICS_INFO_TEXT.STUDENT_PLATFORMS} />,
    dataIndex: "platforms",
    key: "platforms",
    width: STUDENT_TABLE_COLUMN_WIDTHS.platforms,
    responsive: ["lg"],
    render: (platforms) => (
      <>
        {platforms.map((platform) => (
          <Tag key={platform} color="blue">
            {PLATFORM_DISPLAY_NAME_MAP[platform] ?? platform}
          </Tag>
        ))}
      </>
    )
  },
  {
    title: "Last Attempted",
    dataIndex: "lastAttemptedAt",
    key: "lastAttemptedAt",
    width: STUDENT_TABLE_COLUMN_WIDTHS.lastAttemptedAt,
    responsive: ["md"],
    sorter: true,
    sortDirections: ["descend", "ascend"],
    render: (lastAttemptedAt) => dayjs(lastAttemptedAt).format(DATE_DISPLAY_FORMAT)
  }
];

/**
 * Per-student external-problem activity for the requested window, searched,
 * sorted and paginated on the server, with an optional platform filter and an
 * optional resolution filter. Visibility only: there is no block or moderation
 * action here, and the two useful sorts read opposite ways, so neither is
 * highlighted as the "right" one to reach for.
 *
 * @param {object} props
 * @param {string} props.ROOT_URL
 * @param {string} props.startDateIso
 * @param {string} props.endDateIso
 */
const StudentTable = ({ ROOT_URL, startDateIso, endDateIso }) => {
  const [students, setStudents] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState(STUDENT_FILTER_INITIAL_STATE);
  const [pager, setPager] = useState({
    windowKey: null,
    page: EXTERNAL_PROBLEMS_PAGINATION_CONFIG.DEFAULT_PAGE
  });
  const [pageSize, setPageSize] = useState(EXTERNAL_PROBLEMS_PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  const [sortConfig, setSortConfig] = useState(STUDENT_SORT_INITIAL_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const abortControllerRef = useRef(null);
  const screens = Grid.useBreakpoint();

  const isMobileView = screens.md === false;
  // The page belongs to the date window it was chosen in, so a new window reads as page 1
  // in the same render that carries the new window, and the list is fetched once.
  const windowKey = `${startDateIso}|${endDateIso}`;
  const currentPage =
    pager.windowKey === windowKey ? pager.page : EXTERNAL_PROBLEMS_PAGINATION_CONFIG.DEFAULT_PAGE;
  const isEmptyStateVisible = hasLoaded && !isLoading;

  /**
   * Loads the page of students for the chosen window, filters and sort. Only
   * the latest call writes state, and a failure leaves the rows on screen.
   */
  const fetchStudents = useCallback(() => {
    const { platform, resolution, searchTerm } = filters;
    const { sortBy, sortOrder } = sortConfig;

    const { signal } = beginLatestRequest(abortControllerRef);

    setIsLoading(true);
    axios
      .get(`${ROOT_URL}/${EXTERNAL_PROBLEMS_API_BASE}/analytics/students`, {
        signal,
        params: {
          startDate: startDateIso,
          endDate: endDateIso,
          platform,
          resolution,
          searchTerm: searchTerm || undefined,
          page: currentPage,
          limit: pageSize,
          sortBy,
          sortOrder
        }
      })
      .then((response) => {
        const { students: fetchedStudents, pagination } = response.data;
        setStudents(fetchedStudents);
        setTotalItems(pagination.totalItems);
        setHasLoaded(true);
      })
      .catch(reportUnlessCancelled)
      .finally(() => {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      });
  }, [ROOT_URL, startDateIso, endDateIso, filters, currentPage, pageSize, sortConfig]);

  /**
   * Merges a partial filter change into the current filters and returns the
   * pager to its first page.
   *
   * @param {Partial<typeof STUDENT_FILTER_INITIAL_STATE>} filterPatch
   */
  const updateFilter = (filterPatch) => {
    setFilters((previousFilters) => ({ ...previousFilters, ...filterPatch }));
    setPager({ windowKey, page: EXTERNAL_PROBLEMS_PAGINATION_CONFIG.DEFAULT_PAGE });
  };

  /**
   * Mirrors the table's own sorter into the request. antd reports a cleared
   * sorter with no order at all, which is the one case that falls back to the
   * default ordering rather than sending nothing and letting the endpoint pick.
   *
   * @param {object} pagination - antd's pager state.
   * @param {number} pagination.current - Page the table moved to.
   * @param {number} pagination.pageSize - Rows per page after the change.
   * @param {object} _tableFilters - antd's column filters, unused: every filter here is a control above the table.
   * @param {{field?: string, order?: string}} sorter
   */
  const handleTableChange = (
    { current: nextPage, pageSize: nextPageSize },
    _tableFilters,
    { field, order }
  ) => {
    setPageSize(nextPageSize);

    const nextSortBy = STUDENT_TABLE_SORT_COLUMN_MAP[field];
    const nextSortConfig =
      nextSortBy && order
        ? { sortBy: nextSortBy, sortOrder: STUDENT_SORT_ORDER_BY_TABLE_ORDER[order] }
        : STUDENT_SORT_INITIAL_STATE;
    const hasSortChanged =
      nextSortConfig.sortBy !== sortConfig.sortBy || nextSortConfig.sortOrder !== sortConfig.sortOrder;

    if (hasSortChanged) {
      setSortConfig(nextSortConfig);
    }
    setPager({
      windowKey,
      page: hasSortChanged ? EXTERNAL_PROBLEMS_PAGINATION_CONFIG.DEFAULT_PAGE : nextPage
    });
  };

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  useEffect(() => () => abortLatestRequest(abortControllerRef), []);

  return (
    <Card
      title="Per-Student Activity"
      extra={
        <Space wrap size={8}>
          <Input.Search
            placeholder="Search by name or email"
            allowClear
            style={{ minWidth: 220 }}
            onSearch={(searchTerm) => updateFilter({ searchTerm: searchTerm.trim() })}
          />
          <Select
            placeholder="All platforms"
            allowClear
            style={{ minWidth: 160 }}
            options={REGISTERED_PLATFORM_FILTER_OPTIONS}
            onChange={(platform) => updateFilter({ platform })}
          />
          <Space size={4}>
            <Select
              placeholder="All resolutions"
              allowClear
              style={{ minWidth: 160 }}
              options={RESOLUTION_FILTER_OPTIONS}
              onChange={(resolution) => updateFilter({ resolution })}
            />
            <InfoHint title={ANALYTICS_INFO_TEXT.RESOLUTION_FILTER} />
          </Space>
        </Space>
      }
      style={{ marginBottom: 24 }}
    >
      <Text type="secondary">
        Visibility only, no action taken here. Sort by attempts with a high invalid URL share to see
        who is pasting a lot of dead links; sort by not found to see who the catalogue is failing.
      </Text>
      <Table
        style={{ marginTop: 16 }}
        rowKey="userId"
        columns={STUDENT_TABLE_COLUMNS}
        dataSource={students}
        loading={isLoading}
        locale={{
          emptyText: isEmptyStateVisible ? (
            <Empty description="No student attempted an external problem with these filters." />
          ) : (
            <div style={{ minHeight: 120 }} />
          )
        }}
        bordered
        size={isMobileView ? "small" : "middle"}
        onChange={handleTableChange}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalItems,
          showSizeChanger: !isMobileView,
          simple: isMobileView,
          pageSizeOptions: EXTERNAL_PROBLEMS_PAGINATION_CONFIG.PAGE_SIZE_OPTIONS
        }}
        scroll={{ x: isMobileView ? "max-content" : sumColumnWidths(STUDENT_TABLE_COLUMN_WIDTHS) }}
      />
    </Card>
  );
};

export default StudentTable;
